import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, Button, CircularProgress, Alert, Chip, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  TextField, IconButton, Autocomplete, Tooltip, Collapse
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import InventoryIcon from '@mui/icons-material/Inventory'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import SplitscreenIcon from '@mui/icons-material/Splitscreen'
import DownloadIcon from '@mui/icons-material/Download'
import { dcApi } from '../services/dc.api'
import { DCPurchaseItem, MasterMedicine, DCStoreSplitReport } from '../types/dc.types'

const statusColors: Record<string, 'default' | 'primary' | 'success'> = {
  draft: 'default',
  confirmed: 'primary',
  inventoried: 'success'
}

// Inline editable row component
function EditableRow({
  item,
  purchaseId,
  isDraft,
  onSaved
}: {
  item: DCPurchaseItem
  purchaseId: number
  isDraft: boolean
  onSaved: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [matchDialogOpen, setMatchDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<MasterMedicine[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [editValues, setEditValues] = useState({
    batch_no: item.batch_no || '',
    expiry_date: item.expiry_date ? item.expiry_date.split('T')[0] : '',
    quantity: item.quantity || 0,
    free_quantity: item.free_quantity || 0,
    rate: item.rate != null ? Number(item.rate) : 0,
    mrp: item.mrp != null ? Number(item.mrp) : 0,
    discount_pct: item.discount_pct || 0,
  })

  const handleEdit = () => {
    setEditValues({
      batch_no: item.batch_no || '',
      expiry_date: item.expiry_date ? item.expiry_date.split('T')[0] : '',
      quantity: item.quantity || 0,
      free_quantity: item.free_quantity || 0,
      rate: item.rate != null ? Number(item.rate) : 0,
      mrp: item.mrp != null ? Number(item.mrp) : 0,
      discount_pct: item.discount_pct || 0,
    })
    setEditing(true)
  }

  const handleCancel = () => setEditing(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await dcApi.updatePurchaseItem(purchaseId, item.id, {
        batch_no: editValues.batch_no,
        expiry_date: editValues.expiry_date || null,
        quantity: editValues.quantity,
        free_quantity: editValues.free_quantity,
        rate: editValues.rate,
        mrp: editValues.mrp,
        discount_pct: editValues.discount_pct,
      })
      setEditing(false)
      onSaved()
    } catch {
      // error handled by parent
    } finally {
      setSaving(false)
    }
  }

  const handleSearch = async (query: string) => {
    setSearchQuery(query)
    if (query.length < 2) {
      setSearchResults([])
      return
    }
    setSearchLoading(true)
    try {
      const results = await dcApi.searchMedicines(query, 20)
      setSearchResults(results)
    } catch {
      setSearchResults([])
    } finally {
      setSearchLoading(false)
    }
  }

  const handleMatchSelect = async (medicine: MasterMedicine) => {
    setSaving(true)
    try {
      await dcApi.updatePurchaseItem(purchaseId, item.id, {
        medicine_id: medicine.id,
        is_matched: true,
        match_method: 'manual',
        match_confidence: 1.0
      })
      setMatchDialogOpen(false)
      onSaved()
    } catch {
      // error
    } finally {
      setSaving(false)
    }
  }

  const gstPct = (item.cgst_pct || 0) + (item.sgst_pct || 0) + (item.igst_pct || 0)

  if (editing) {
    return (
      <TableRow sx={{ backgroundColor: 'action.hover' }}>
        <TableCell sx={{ minWidth: 160 }}>
          <Typography variant="body2" fontWeight={500}>{item.product_name}</Typography>
        </TableCell>
        <TableCell>{item.pack_size || '-'}</TableCell>
        <TableCell>
          <TextField
            size="small"
            value={editValues.batch_no}
            onChange={e => setEditValues(v => ({ ...v, batch_no: e.target.value }))}
            sx={{ width: 100 }}
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            type="date"
            value={editValues.expiry_date}
            onChange={e => setEditValues(v => ({ ...v, expiry_date: e.target.value }))}
            sx={{ width: 140 }}
            InputLabelProps={{ shrink: true }}
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={editValues.quantity}
            onChange={e => setEditValues(v => ({ ...v, quantity: parseInt(e.target.value) || 0 }))}
            sx={{ width: 60 }}
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={editValues.free_quantity}
            onChange={e => setEditValues(v => ({ ...v, free_quantity: parseInt(e.target.value) || 0 }))}
            sx={{ width: 60 }}
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={editValues.rate}
            onChange={e => setEditValues(v => ({ ...v, rate: parseFloat(e.target.value) || 0 }))}
            sx={{ width: 80 }}
            inputProps={{ step: '0.01' }}
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={editValues.mrp}
            onChange={e => setEditValues(v => ({ ...v, mrp: parseFloat(e.target.value) || 0 }))}
            sx={{ width: 80 }}
            inputProps={{ step: '0.01' }}
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={editValues.discount_pct}
            onChange={e => setEditValues(v => ({ ...v, discount_pct: parseFloat(e.target.value) || 0 }))}
            sx={{ width: 60 }}
            inputProps={{ step: '0.1' }}
          />
        </TableCell>
        <TableCell align="right">{gstPct > 0 ? `${gstPct}%` : '-'}</TableCell>
        <TableCell align="right">
          {(editValues.rate * editValues.quantity).toFixed(2)}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={handleCancel}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ maxWidth: 200 }}>
          <Typography variant="body2" noWrap title={item.product_name}>
            {item.product_name}
          </Typography>
        </TableCell>
        <TableCell>{item.pack_size || '-'}</TableCell>
        <TableCell>{item.batch_no || '-'}</TableCell>
        <TableCell>
          {item.expiry_date
            ? new Date(item.expiry_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
            : '-'}
        </TableCell>
        <TableCell align="right">{item.quantity}</TableCell>
        <TableCell align="right">{item.free_quantity || 0}</TableCell>
        <TableCell align="right">
          {item.rate != null ? Number(item.rate).toFixed(2) : '-'}
        </TableCell>
        <TableCell align="right">
          {item.mrp != null ? Number(item.mrp).toFixed(2) : '-'}
        </TableCell>
        <TableCell align="right">
          {item.discount_pct ? `${item.discount_pct}%` : '-'}
        </TableCell>
        <TableCell align="right">
          {gstPct > 0 ? `${gstPct}%` : '-'}
        </TableCell>
        <TableCell align="right">
          {item.line_total != null
            ? Number(item.line_total).toLocaleString('en-IN', { minimumFractionDigits: 2 })
            : '-'}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {item.is_matched ? (
              <Tooltip title={item.matched_medicine_name || 'Matched'}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100 }}>
                    {item.matched_medicine_name || 'Matched'}
                  </Typography>
                </Box>
              </Tooltip>
            ) : (
              <Tooltip title="Click to match">
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: isDraft ? 'pointer' : 'default' }}
                  onClick={() => isDraft && setMatchDialogOpen(true)}
                >
                  <CancelIcon color="error" fontSize="small" />
                  <Typography variant="caption" color="error">Unmatched</Typography>
                </Box>
              </Tooltip>
            )}
            {isDraft && (
              <Box sx={{ display: 'flex', ml: 'auto' }}>
                <Tooltip title="Search & match medicine">
                  <IconButton size="small" onClick={() => { setSearchQuery(item.product_name); setMatchDialogOpen(true) }}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit fields">
                  <IconButton size="small" onClick={handleEdit}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>

      {/* Medicine Match Dialog */}
      <Dialog open={matchDialogOpen} onClose={() => setMatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Match Medicine — {item.product_name}</DialogTitle>
        <DialogContent>
          <Autocomplete
            freeSolo
            options={searchResults}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : `${option.medicine_name} (${option.pack_size || '-'}) — ${option.manufacturer_name || ''}`
            }
            inputValue={searchQuery}
            onInputChange={(_, val) => handleSearch(val)}
            onChange={(_, val) => {
              if (val && typeof val !== 'string') handleMatchSelect(val)
            }}
            loading={searchLoading}
            renderOption={(props, option) => (
              <li {...props} key={typeof option === 'string' ? option : option.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {typeof option === 'string' ? option : option.medicine_name}
                  </Typography>
                  {typeof option !== 'string' && (
                    <Typography variant="caption" color="text.secondary">
                      {option.pack_size || '-'} | {option.manufacturer_name || '-'}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search medicine master"
                placeholder="Type medicine name..."
                autoFocus
                sx={{ mt: 1 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
          {searchResults.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {searchResults.length} results — click to select
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default function DCPurchaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [splitOpen, setSplitOpen] = useState(false)
  const [splitError, setSplitError] = useState<string | null>(null)
  const [splitData, setSplitData] = useState<DCStoreSplitReport | null>(null)
  const [splitLoading, setSplitLoading] = useState(false)
  const [exportingExcel, setExportingExcel] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['purchase', id],
    queryFn: () => dcApi.getPurchase(parseInt(id!)),
    enabled: !!id
  })

  const handleStoreSplit = async () => {
    if (splitOpen && splitData) { setSplitOpen(false); return }
    setSplitLoading(true)
    setSplitError(null)
    try {
      const result = await dcApi.getStoreSplit(parseInt(id!))
      setSplitData(result)
      setSplitOpen(true)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load store split report'
      // Try to extract message from axios error response
      const axiosMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setSplitError(axiosMsg || msg)
      setSplitOpen(true)
    } finally {
      setSplitLoading(false)
    }
  }

  const handleExcelExport = async () => {
    setExportingExcel(true)
    try {
      const blob = await dcApi.exportStoreSplitExcel(parseInt(id!))
      const purchase = data?.purchase
      const distName = (purchase?.distributor_name || purchase?.distributor_db_name || 'distributor')
        .toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
      const filename = `${purchase?.purchase_number || `purchase_${id}`}_${distName}_store_split.xlsx`
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err: unknown) {
      const axiosMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setSnackbar({ open: true, message: axiosMsg || 'Failed to export Excel', severity: 'error' })
    } finally {
      setExportingExcel(false)
    }
  }

  const confirmMutation = useMutation({
    mutationFn: () => dcApi.confirmPurchase(parseInt(id!)),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['purchase', id] })
      queryClient.invalidateQueries({ queryKey: ['purchases'] })
      queryClient.invalidateQueries({ queryKey: ['inventory'] })
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] })
      setSnackbar({
        open: true,
        message: `Purchase confirmed. ${result.inventoryItemsCreated} items added to inventory.`,
        severity: 'success'
      })
    },
    onError: (err: Error) => {
      setSnackbar({ open: true, message: `Failed to confirm: ${err.message}`, severity: 'error' })
    }
  })

  const deleteMutation = useMutation({
    mutationFn: () => dcApi.deletePurchase(parseInt(id!)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] })
      navigate('/purchases')
    },
    onError: (err: Error) => {
      setSnackbar({ open: true, message: `Failed to delete: ${err.message}`, severity: 'error' })
    }
  })

  const handleItemSaved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['purchase', id] })
  }, [queryClient, id])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error || !data) {
    return <Alert severity="error">Failed to load purchase: {(error as Error)?.message || 'Not found'}</Alert>
  }

  const { purchase, items } = data
  const isDraft = purchase.status === 'draft'

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/purchases')}>
            Back
          </Button>
          <Typography variant="h5">{purchase.purchase_number}</Typography>
          <Chip
            label={purchase.status}
            size="small"
            color={statusColors[purchase.status] || 'default'}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {purchase.status === 'inventoried' && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={splitLoading ? <CircularProgress size={16} /> : <SplitscreenIcon />}
              onClick={handleStoreSplit}
              disabled={splitLoading}
            >
              {splitLoading ? 'Loading...' : splitOpen ? 'Hide Split Report' : 'Store Split Report'}
            </Button>
          )}
          {isDraft && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={confirmMutation.isPending ? <CircularProgress size={16} /> : <InventoryIcon />}
                onClick={() => confirmMutation.mutate()}
                disabled={confirmMutation.isPending}
              >
                {confirmMutation.isPending ? 'Confirming...' : 'Confirm & Add to Inventory'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteConfirm(true)}
              >
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="body2" color="text.secondary">Invoice #</Typography>
            <Typography variant="body1" fontWeight={500}>{purchase.invoice_number || '-'}</Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="body2" color="text.secondary">Invoice Date</Typography>
            <Typography variant="body1" fontWeight={500}>
              {purchase.invoice_date
                ? new Date(purchase.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="body2" color="text.secondary">Distributor</Typography>
            <Typography variant="body1" fontWeight={500}>
              {purchase.distributor_name || purchase.distributor_db_name || '-'}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="body2" color="text.secondary">Total Items</Typography>
            <Typography variant="body1" fontWeight={500}>{purchase.total_items}</Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="body2" color="text.secondary">Matched Items</Typography>
            <Typography variant="body1" fontWeight={500}>{purchase.matched_items}</Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="body2" color="text.secondary">Total Amount</Typography>
            <Typography variant="body1" fontWeight={500}>
              Rs. {(purchase.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Store Split Report Panel */}
      <Collapse in={splitOpen}>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'secondary.light' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Store Split Report</Typography>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={exportingExcel ? <CircularProgress size={14} /> : <DownloadIcon />}
              onClick={handleExcelExport}
              disabled={exportingExcel || !!splitError}
            >
              {exportingExcel ? 'Downloading...' : 'Download Excel'}
            </Button>
          </Box>

          {splitError ? (
            <Alert severity="error">{splitError}</Alert>
          ) : splitData ? (
            <>
              {/* Transfer summary chips */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {splitData.transfers.map(t => (
                  <Chip key={t.id} label={`${t.transfer_number} — ${t.store_name}`} size="small" color="success" variant="outlined" />
                ))}
              </Box>

              {/* Totals bar */}
              <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                {[
                  { label: 'Vadasery', val: splitData.totals.vadasery, color: '#1976d2' },
                  { label: 'KP Road', val: splitData.totals.kproad, color: '#388e3c' },
                  { label: 'Kottar', val: splitData.totals.kottar, color: '#f57c00' },
                  { label: 'Total', val: splitData.totals.total, color: 'text.primary' },
                ].map(s => (
                  <Box key={s.label} sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: s.color, fontWeight: 700 }}>{s.val}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  </Box>
                ))}
              </Box>

              <TableContainer sx={{ maxHeight: 480 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Medicine Name</TableCell>
                      <TableCell>Pack</TableCell>
                      <TableCell>Batch</TableCell>
                      <TableCell>Expiry</TableCell>
                      <TableCell align="right">Total Qty</TableCell>
                      <TableCell align="right" sx={{ color: '#1976d2', fontWeight: 700 }}>Vadasery</TableCell>
                      <TableCell align="right" sx={{ color: '#388e3c', fontWeight: 700 }}>KP Road</TableCell>
                      <TableCell align="right" sx={{ color: '#f57c00', fontWeight: 700 }}>Kottar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {splitData.rows.map((row, i) => (
                      <TableRow key={row.purchase_item_id} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {row.medicine_name || row.product_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.pack_size || '-'}</TableCell>
                        <TableCell>{row.batch_no || '-'}</TableCell>
                        <TableCell>
                          {row.expiry_date
                            ? new Date(row.expiry_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                            : '-'}
                        </TableCell>
                        <TableCell align="right">{row.transferred_qty}</TableCell>
                        <TableCell align="right" sx={{ color: '#1976d2', fontWeight: row.vadasery_qty > 0 ? 600 : 400 }}>
                          {row.vadasery_qty || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#388e3c', fontWeight: row.kproad_qty > 0 ? 600 : 400 }}>
                          {row.kproad_qty || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#f57c00', fontWeight: row.kottar_qty > 0 ? 600 : 400 }}>
                          {row.kottar_qty || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals row */}
                    <TableRow sx={{ backgroundColor: 'action.hover', fontWeight: 700 }}>
                      <TableCell colSpan={5}><strong>TOTAL</strong></TableCell>
                      <TableCell align="right"><strong>{splitData.totals.total}</strong></TableCell>
                      <TableCell align="right" sx={{ color: '#1976d2' }}><strong>{splitData.totals.vadasery}</strong></TableCell>
                      <TableCell align="right" sx={{ color: '#388e3c' }}><strong>{splitData.totals.kproad}</strong></TableCell>
                      <TableCell align="right" sx={{ color: '#f57c00' }}><strong>{splitData.totals.kottar}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
        </Paper>
      </Collapse>

      {/* Items Table */}
      <Typography variant="h6" gutterBottom>
        Items ({items.length})
        {isDraft && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Click edit or search icons to modify items
          </Typography>
        )}
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product Name</TableCell>
              <TableCell>Pack Size</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Free</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">MRP</TableCell>
              <TableCell align="right">Disc%</TableCell>
              <TableCell align="right">GST%</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Match / Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No items found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item: DCPurchaseItem) => (
                <EditableRow
                  key={item.id}
                  item={item}
                  purchaseId={parseInt(id!)}
                  isDraft={isDraft}
                  onSaved={handleItemSaved}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Delete Purchase Bill?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {purchase.purchase_number}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  )
}
